import { useCallback, useEffect, useRef, useState } from "react";
import { useUndoableDocument } from "./useUndoableDocument";

const AUTOSAVE_MS = 1200;
const POLL_MS = 15000;

// Synchronisation d'un document d'offre versionné (chiffrage ou proposition) :
// sauvegarde automatique, jeton de concurrence, conflit, rafraîchissement quand
// un autre (ou Claude via MCP) modifie le document, Ctrl+Z / Ctrl+Y, versions.
//
// api : { get(id, revisionId), save(id, body), restore(id, revisionId, token),
//         revisions(id), label(id, revisionId, label), updateMeta(id, patch) }
// docKey : clé du document dans la réponse ("estimate" ou "proposal").
export const useVersionedDocument = ({ id, api, docKey, watchRevisions = false }) => {
  const { document: content, apply, reset, undo, redo, canUndo, canRedo } = useUndoableDocument(null);

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [status, setStatusState] = useState("saved");
  const [message, setMessage] = useState("");
  const [conflictToken, setConflictToken] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [revisions, setRevisions] = useState([]);
  // Dernière modification arrivée d'ailleurs (Nexus, collègue) : l'éditeur la met en évidence.
  const [remoteChange, setRemoteChange] = useState(null);

  // Les refs portent l'état de synchronisation lu par les minuteries et les requêtes en vol.
  const tokenRef = useRef(null);
  const savedRef = useRef(null);
  const contentRef = useRef(null);
  const statusRef = useRef("saved");
  const viewingRef = useRef(null);
  const inFlightRef = useRef(null);
  contentRef.current = content;
  viewingRef.current = viewing;

  const setStatus = useCallback((next) => {
    statusRef.current = next;
    setStatusState(next);
  }, []);

  const archived = Boolean(data?.[docKey]?.archived);
  const canEdit = Boolean(data?.can_edit);
  const editable = canEdit && !archived && !viewing;
  const editableRef = useRef(false);
  editableRef.current = editable;
  const isDirty = content !== null && JSON.stringify(content) !== savedRef.current;

  const load = useCallback(async () => {
    try {
      const result = await api.get(id);
      tokenRef.current = result.revision_token;
      savedRef.current = JSON.stringify(result.content);
      setData(result);
      reset(result.content);
      setViewing(null);
      setConflictToken(null);
      setStatus("saved");
      setLoadError("");
    } catch (error) {
      setLoadError(error.message);
    }
  }, [api, id, reset, setStatus]);

  useEffect(() => { load(); }, [load]);

  // Contenu venu du serveur (agent, collègue, restauration) : il devient une étape
  // d'annulation, Ctrl+Z permet donc de revenir à la saisie précédente.
  const acceptRemote = useCallback((result, { highlight = true } = {}) => {
    if (highlight) {
      setRemoteChange({ previous: contentRef.current, next: result.content, revision: result.revision, at: Date.now() });
    }
    tokenRef.current = result.revision_token;
    savedRef.current = JSON.stringify(result.content);
    setData(result);
    apply(() => result.content);
    setConflictToken(null);
    setStatus("saved");
  }, [apply, setStatus]);

  const handleConflict = useCallback((error) => {
    if (error.status === 409 && error.detail?.current_token) {
      setConflictToken(error.detail.current_token);
      setStatus("conflict");
      return true;
    }
    return false;
  }, [setStatus]);

  const save = useCallback(() => {
    if (inFlightRef.current) return inFlightRef.current;
    const run = async () => {
      // Boucle : les modifications faites pendant l'envoi partent dans la foulée.
      for (;;) {
        const snapshot = contentRef.current;
        const text = JSON.stringify(snapshot);
        if (!snapshot || text === savedRef.current) {
          if (statusRef.current !== "conflict") setStatus("saved");
          return true;
        }
        setStatus("saving");
        try {
          const result = await api.save(id, { base_token: tokenRef.current, content: snapshot });
          tokenRef.current = result.revision_token;
          savedRef.current = text;
          setData(result);
        } catch (error) {
          if (!handleConflict(error)) {
            setMessage(error.message);
            setStatus("error");
          }
          return false;
        }
      }
    };
    inFlightRef.current = run().finally(() => { inFlightRef.current = null; });
    return inFlightRef.current;
  }, [api, id, handleConflict, setStatus]);

  // Sauvegarde automatique après une pause de saisie.
  useEffect(() => {
    if (!editable || !isDirty || statusRef.current === "conflict") return undefined;
    if (statusRef.current !== "saving") setStatus("dirty");
    const timer = setTimeout(save, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [content, editable, isDirty, save, setStatus]);

  // Rafraîchissement quand quelqu'un d'autre (ou Claude via MCP) modifie le document.
  const loaded = Boolean(data);
  useEffect(() => {
    if (!loaded) return undefined;
    const check = async () => {
      const idle = () => !inFlightRef.current && !viewingRef.current && statusRef.current !== "conflict"
        && JSON.stringify(contentRef.current) === savedRef.current;
      if (window.document.hidden || !idle()) return;
      try {
        const result = await api.get(id);
        if (result.revision_token !== tokenRef.current) {
          if (idle()) acceptRemote(result);
        } else {
          // Même version : seules les données autour du contenu bougent (prix du chiffrage lié, droits…).
          setData((previous) => ({ ...result, content: previous?.content ?? result.content }));
        }
      } catch (error) {
        // Réseau indisponible : on réessaiera au prochain passage.
      }
    };
    const timer = setInterval(check, POLL_MS);
    // En revenant sur l'onglet, on regarde tout de suite ce qui a changé pendant l'absence.
    const onVisible = () => { if (!window.document.hidden) check(); };
    window.document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      window.document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loaded, api, id, acceptRemote]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (JSON.stringify(contentRef.current) !== savedRef.current && editableRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (editableRef.current && statusRef.current !== "conflict") save();
    };
  }, [save]);

  // Ctrl+Z / Ctrl+Y / Ctrl+Maj+Z sur tout le document.
  useEffect(() => {
    const onKey = (event) => {
      if (!editableRef.current || !(event.ctrlKey || event.metaKey)) return;
      const target = event.target;
      if (target?.closest?.("[data-own-undo]")) return;
      const key = event.key.toLowerCase();
      const isUndo = key === "z" && !event.shiftKey;
      const isRedo = key === "y" || (key === "z" && event.shiftKey);
      if (!isUndo && !isRedo) return;
      event.preventDefault();
      // Le champ actif garde sa saisie tant qu'il a le focus : on le libère d'abord.
      const active = window.document.activeElement;
      if (active?.tagName === "INPUT" || active?.tagName === "TEXTAREA") active.blur();
      if (isUndo) undo();
      else redo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const refreshRevisions = useCallback(async () => {
    try {
      const result = await api.revisions(id);
      setRevisions(result.items || []);
    } catch (error) {
      setMessage(error.message);
    }
  }, [api, id]);

  const currentToken = data?.revision_token;
  useEffect(() => {
    if (watchRevisions) refreshRevisions();
  }, [watchRevisions, currentToken, refreshRevisions]);

  const loadLatest = async () => {
    try {
      acceptRemote(await api.get(id));
    } catch (error) {
      setMessage(error.message);
    }
  };

  const keepMine = () => {
    tokenRef.current = conflictToken;
    setConflictToken(null);
    setStatus("dirty");
    save();
  };

  const viewRevision = async (revisionId) => {
    try {
      await save();
      const result = await api.get(id, revisionId);
      setViewing(result.revision.is_current ? null : result);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const restoreRevision = async (revisionId) => {
    if (!(await save())) return;
    try {
      acceptRemote(await api.restore(id, revisionId, tokenRef.current), { highlight: false });
      setViewing(null);
    } catch (error) {
      if (!handleConflict(error)) setMessage(error.message);
    }
  };

  const labelRevision = async (revisionId, label) => {
    try {
      const result = await api.label(id, revisionId, label);
      setRevisions(result.items || []);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const updateMeta = async (patch) => {
    try {
      if (patch.archived) await save();
      const result = await api.updateMeta(id, patch);
      setData((previous) => ({ ...result, content: previous?.content ?? result.content }));
      return result;
    } catch (error) {
      setMessage(error.message);
      return null;
    }
  };

  return {
    data, setData, loadError, content, shown: viewing ? viewing.content : content,
    apply, undo, redo, canUndo, canRedo,
    status, message, setMessage, save, loadLatest, keepMine,
    canEdit, archived, editable,
    viewing, setViewing, viewRevision, restoreRevision, labelRevision, revisions, refreshRevisions,
    updateMeta, tokenRef, contentRef, remoteChange,
  };
};
